import { useRef, useCallback, useState, useEffect, useMemo } from "react";
import * as THREE from "three";
import { useSpring, config } from "@react-spring/core";
import { useGesture } from "react-use-gesture";
import ResizeObserver from "resize-observer-polyfill";
import clamp from "lodash/clamp";
import { accessors } from "./parser";
import gsap from "gsap";
import { useThree } from "react-three-fiber";
import { lambdaConstants } from "./constants";
// import { indexOfAll } from "./data";

export const useMeasure = () => {
  const ref = useRef();
  const [bounds, set] = useState({ left: 0, top: 0, width: 0, height: 0 });
  const [ro] = useState(
    () => new ResizeObserver(([entry]) => set(entry.contentRect))
  );
  useEffect(() => (ro.observe(ref.current), ro.disconnect), [ro]);
  return [{ ref }, bounds];
};

export function useKeyPress(targetKey) {
  // State for keeping track of whether key is pressed
  const [keyPressed, setKeyPressed] = useState(false);

  // If pressed key is our target key then set to true
  function downHandler({ key }) {
    if (key === targetKey) {
      setKeyPressed(true);
    }
  }

  // If released key is our target key then set to false
  const upHandler = ({ key }) => {
    if (key === targetKey) {
      setKeyPressed(false);
    }
  };

  // Add event listeners
  useEffect(() => {
    window.addEventListener("keydown", downHandler);
    window.addEventListener("keyup", upHandler);
    // Remove event listeners on cleanup
    return () => {
      window.removeEventListener("keydown", downHandler);
      window.removeEventListener("keyup", upHandler);
    };
  }, []); // Empty array ensures that effect is only run on mount and unmount

  return keyPressed;
}

/**
 * Use like:
 * const [y] = useYScroll([-100, 3 * window.innerHeight], { domTarget: window })
 *
 * And then:
 * y.interpolate(
 *  [0, 3 * window.innerHeight],
 *  [-50, CHART_DIMENSIONS.X.MAX * scaleZ]
 * )
 * in animated props
 * @param {*} bounds 1D array of height bounds
 * @param {*} props used to specify domTarget
 * @returns
 */
export function useYScroll(bounds, props) {
  const [{ y }, set] = useSpring(() => ({ y: 0, config: config.slow }));
  const fn = useCallback(
    ({ xy: [, cy], previous: [, py], memo = y.get() }) => {
      const newY = clamp(memo + cy - py, ...bounds);
      set({ y: newY });
      return newY;
    },
    [bounds, y, set]
  );
  const bind = useGesture({ onWheel: fn }, props);
  useEffect(() => props && props.domTarget && bind(), [props, bind]);
  return [y, bind];
}

export const useMemoAttributeArrays = ({
  colorFunc = accessors.color,
  data = [],
  opacityFunc = accessors.opacity,
}) => {
  const color = useMemo(() => new THREE.Color(), []);
  const {
    colorArray,
    opacityArray,
    idArray,
    animationDelayArray,
    renderFuzzinessArray,
  } = useMemo(
    () => {
      // console.log("computing memoized attribute arrays");
      const colorArray = Float32Array.from(
        new Array(data.length)
          .fill()
          .flatMap((_, i) => color.set(colorFunc(data[i])).toArray())
      );

      const opacityArray = Float32Array.from(data.flatMap(opacityFunc));
      const idArray = Float32Array.from(data.flatMap((d) => accessors.id(d)));
      const animationDelayArray = Float32Array.from(data.flatMap((d) => 0));
      const renderFuzzinessArray = Float32Array.from(
        data.flatMap((d) => Number(d.renderFuzziness))
      );
      return {
        colorArray,
        opacityArray,
        idArray,
        animationDelayArray,
        renderFuzzinessArray,
      };
    },
    // TODO Is there a difference between the two dependency arrays?
    []
    // [colorFunc, data, opacityFunc, color]
  );

  return {
    colorArray,
    opacityArray,
    idArray,
    animationDelayArray,
    renderFuzzinessArray,
  };
};

export const useResetCamera = ({ reset = false, cameraControlsRef = null }) => {
  const { camera } = useThree();
  useSpring({
    from: {
      position: [camera.position.x, camera.position.y, camera.position.z],
      rotation: [camera.rotation.x, camera.rotation.y, camera.rotation.z],
    },
    to: {
      position: [
        reset ? 0 : camera.position.x,
        reset ? 0 : camera.position.y,
        reset ? 0 : camera.position.z,
      ],
      rotation: [
        reset ? 0 : camera.rotation.x,
        reset ? 0 : camera.rotation.y,
        reset ? 0 : camera.rotation.z,
      ],
    },
    onStart: () => {
      cameraControlsRef.current && (cameraControlsRef.current.enabled = false);
    },
    onRest: () => {
      cameraControlsRef.current && (cameraControlsRef.current.enabled = true);
    },
    onChange: ({ position, rotation }) => {
      camera.position.set(...position);
      camera.rotation.set(...rotation);
      camera.updateMatrix();
    },
  });
};

// All time durations (including for attributes) are in SECONDS!
export const useTransitionAttributeArray = ({
  data = [],
  selected = [],
  idFunc = accessors.attributeID,
  opacityFunc = accessors.opacity,
  delayFunc = lambdaConstants.delayFunc,
  duration = 0.4,
  meshRef,
}) => {
  const { clock } = useThree();
  useEffect(() => {
    if (!meshRef.current) return;
    // console.time("useTransitionAttributeArray");
    const selectedIdArray = selected.map(idFunc);
    const allIdArray = data.map(idFunc);
    const targetOpacityArray = Float32Array.from(data.map((d) => 0));
    const animationDelayArray = Float32Array.from(data.map(delayFunc));
    for (let i = 0; i < selectedIdArray.length; i++) {
      const idInDataArray = allIdArray.indexOf(selectedIdArray[i]);
      targetOpacityArray[idInDataArray] = opacityFunc(selected[i]);
    }

    // UPDATE target attributes AFTER resetting clock markers
    meshRef.current.geometry.attributes.instanceOpacity.array =
      meshRef.current.geometry.attributes.tOpacity.array;
    meshRef.current.geometry.attributes.instanceOpacity.needsUpdate = true;

    meshRef.current.geometry.attributes.tOpacity.array = targetOpacityArray;
    meshRef.current.geometry.attributes.tOpacity.needsUpdate = true;

    meshRef.current.geometry.attributes.animationDelay.array =
      animationDelayArray;

    meshRef.current.geometry.attributes.animationDelay.needsUpdate = true;
    meshRef.current.material.animationStart = clock.elapsedTime;
    meshRef.current.material.animationStop = clock.elapsedTime + duration;

    // update the material's uniform
    gsap.fromTo(
      meshRef.current.material,
      { animationDelta: 0 },
      {
        animationDelta: 1,
        ease: "power3.out",
        duration,
        overwrite: true,
      }
    );
    // console.timeEnd("useTransitionAttributeArray");

    // gsap.to(meshRef.current.geometry.attributes.instanceOpacity.array, {
    //   endArray: targetOpacityArray,
    //   duration: 1,
    //   ease: "power3.out",
    //   // Make sure to tell it to update
    //   onUpdate: () =>
    //     (meshRef.current.geometry.attributes.instanceOpacity.needsUpdate = true),
    // });
  }, [data, meshRef, idFunc, duration, selected, delayFunc, opacityFunc]);
};
